import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { LoginService } from '../../login/login.service';
import { PermissionsService } from './permissions.service';

/**
 * Route guard reading `data: { permissions: [...] }` off the route config. Any-of:
 * the route activates when the user holds at least one of the listed slugs.
 *
 * Failure redirects to wherever this user belongs instead of leaving a blank page,
 * which is what the role guards it replaces used to do.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard {
  private readonly permissions = inject(PermissionsService);
  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    return this.check(route);
  }

  canActivateChild(route: ActivatedRouteSnapshot): boolean | UrlTree {
    return this.check(route);
  }

  private check(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const required: string[] = route.data?.['permissions'] ?? [];
    if (required.length === 0 || this.permissions.hasAny(required)) {
      return true;
    }
    return this.fallback();
  }

  private fallback(): UrlTree {
    const target = this.loginService.resolvePostLoginTarget();
    return 'navigateByUrl' in target
      ? this.router.parseUrl(target.navigateByUrl)
      : this.router.createUrlTree(target.navigate);
  }
}
